import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, CheckCircle2, KeyRound, Loader2, UserRound } from 'lucide-react'
import { apiMasterResetAgentPassword, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/components/PasswordInput'
import { useT } from '@/contexts/language'

const resetSchema = z.object({
  newPassword: z.string().min(8, 'At least 8 characters').max(128),
})
type ResetForm = z.infer<typeof resetSchema>

/**
 * The master's view of an agent's login: which username it signs in with, and a way to
 * set a new password when the agent has lost theirs. The server works out the account
 * from the agent, so nothing here names a member id.
 */
export function AgentLoginCard({ agentId, username }: { agentId: string; username: string | null }) {
  const t = useT()
  const [done, setDone] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({ resolver: zodResolver(resetSchema), defaultValues: { newPassword: '' } })

  async function onReset(values: ResetForm) {
    setSubmitError(null)
    setDone(false)
    try {
      await apiMasterResetAgentPassword(agentId, values.newPassword)
      reset({ newPassword: '' })
      setDone(true)
    } catch (err) {
      setSubmitError(err instanceof ApiRequestError ? err.message : t.agents.resetPasswordError)
    }
  }

  return (
    <section
      aria-labelledby="agent-login-heading"
      className="rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-[var(--shadow-sm)]"
    >
      <div className="px-4 py-2.5 bg-secondary">
        <h2
          id="agent-login-heading"
          className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground"
        >
          {t.agents.loginAccount}
        </h2>
      </div>

      {username === null ? (
        <div className="py-8 text-center">
          <p className="font-mono text-sm text-muted-foreground">{t.agents.noLoginAccount}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onReset)} noValidate className="p-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-xs text-muted-foreground">{t.common.username}</span>
            <div className="h-9 flex items-center gap-2 rounded-[var(--radius-sm)] border border-dashed border-border bg-muted/40 px-3">
              <UserRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
              <span className="font-mono text-sm font-semibold text-foreground">{username}</span>
            </div>
            <p className="font-mono text-[10px] text-muted-foreground">{t.agents.loginAccountHint}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="newPassword" className="font-mono text-xs">
              {t.agents.newPassword}
            </Label>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              className="font-mono text-sm"
              aria-invalid={!!errors.newPassword}
              showLabel={t.agents.showPassword}
              hideLabel={t.agents.hidePassword}
              {...register('newPassword')}
            />
            {errors.newPassword?.message && (
              <p className="font-mono text-xs text-destructive flex items-center gap-1" role="alert">
                <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                {errors.newPassword.message}
              </p>
            )}
            <p className="font-mono text-[10px] text-muted-foreground">{t.agents.resetPasswordHint}</p>
          </div>

          {submitError && (
            <div
              className="flex items-start gap-2 p-3 border border-destructive/40 rounded-[var(--radius-sm)] bg-destructive/10"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
              <p className="font-mono text-xs text-destructive">{submitError}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            {done && (
              <span className="font-mono text-xs text-success flex items-center gap-1" role="status">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                {t.agents.resetPasswordDone}
              </span>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="h-8 gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider"
            >
              {isSubmitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {t.agents.resetPassword}
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}
